import { useState, useCallback } from 'react'
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Chip, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Checkbox, FormGroup, Autocomplete,
} from '@mui/material'
import { Add, Delete, Edit, QrCode2, Share } from '@mui/icons-material'
import { useApi } from '../../api/client'
import {
  fetchSettingsCustomActions, createCustomAction, updateCustomAction, deleteCustomAction,
} from '../../api/custom-actions'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { useToastStore } from '../../stores/toast.store'
import { ICON_OPTIONS, getIconComponent } from './ObjectManagement'
import type { CustomActionWithObject, ActionType, ActionVisibility } from '@crm/shared'

interface ObjectItem {
  id: string
  name: string
  label: string
  is_system: number
}

interface RoleItem {
  id: string
  name: string
  label: string
}

const ACTION_TYPE_OPTIONS: { value: ActionType; label: string; icon: React.ReactElement }[] = [
  { value: 'qr_code', label: 'QR Code', icon: <QrCode2 fontSize="small" /> },
  { value: 'share', label: 'Share', icon: <Share fontSize="small" /> },
]

const EMPTY_FORM = {
  object_id: '',
  name: '',
  label: '',
  action_type: 'qr_code' as ActionType,
  template: '',
  icon: '',
  sort_order: 0,
  visibility: 'all_roles' as ActionVisibility,
  role_ids: [] as string[],
}

export function CustomActionManagement() {
  const { data, mutate } = useApi<{ data: CustomActionWithObject[] }>('/settings/custom-actions')
  const { data: objectsData } = useApi<{ data: ObjectItem[] }>('/objects')
  const { data: rolesData } = useApi<{ data: RoleItem[] }>('/settings/roles')
  const toast = useToastStore()

  const actions = data?.data ?? []
  const objects = objectsData?.data ?? []
  const roles = rolesData?.data ?? []

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CustomActionWithObject | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const sortedActions = [...actions].sort((a, b) => {
    const labelCompare = a.object_label.localeCompare(b.object_label)
    if (labelCompare !== 0) return labelCompare
    return a.sort_order - b.sort_order
  })

  const openCreateDialog = useCallback(() => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setDialogOpen(true)
  }, [])

  const openEditDialog = useCallback((action: CustomActionWithObject) => {
    setEditing(action)
    setForm({
      object_id: action.object_id,
      name: action.name,
      label: action.label,
      action_type: action.action_type,
      template: action.template ?? '',
      icon: action.icon ?? '',
      sort_order: action.sort_order,
      visibility: action.visibility,
      role_ids: [],
    })
    setDialogOpen(true)
  }, [])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      if (editing) {
        await updateCustomAction(editing.id, {
          object_id: form.object_id || undefined,
          name: form.name || undefined,
          label: form.label || undefined,
          action_type: form.action_type,
          template: form.template || undefined,
          icon: form.icon || undefined,
          sort_order: form.sort_order,
          visibility: form.visibility,
          role_ids: form.visibility === 'specific_roles' ? form.role_ids : undefined,
        })
      } else {
        await createCustomAction({
          object_id: form.object_id,
          name: form.name,
          label: form.label,
          action_type: form.action_type,
          template: form.template || undefined,
          icon: form.icon || undefined,
          sort_order: form.sort_order,
          visibility: form.visibility,
          role_ids: form.visibility === 'specific_roles' ? form.role_ids : undefined,
        })
      }
      mutate()
      setDialogOpen(false)
      toast.success(editing ? '自定义动作已更新' : '自定义动作已创建')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }, [editing, form, mutate])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteCustomAction(id)
      mutate()
      toast.success('自定义动作已删除')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '删除失败')
    }
    setDeleteConfirm(null)
  }, [mutate, toast])

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  const isFormValid = form.object_id && form.name.trim() && form.label.trim()

  const selectedObjectFields = form.object_id
    ? (objects.find((o) => o.id === form.object_id)?.name ?? '')
    : ''

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Custom Actions</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={openCreateDialog}>
          Add Action
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add custom buttons to object detail views. Actions appear in the TI header area and support QR Code generation and social sharing.
      </Typography>

      {sortedActions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <QrCode2 sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">No custom actions configured yet.</Typography>
          <Typography variant="body2" color="text.secondary">
            Click &quot;Add Action&quot; to create a custom button for an object&apos;s detail view.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Object</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Visibility</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sort</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedActions.map((action) => {
                const obj = objects.find((o) => o.id === action.object_id)
                return (
                  <TableRow key={action.id}>
                    <TableCell>
                      <Chip
                        label={obj?.label ?? action.object_label}
                        size="small"
                        variant={obj?.is_system ? 'filled' : 'outlined'}
                        color={obj?.is_system ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{action.label}</TableCell>
                    <TableCell>
                      <Chip
                        icon={action.action_type === 'qr_code' ? <QrCode2 /> : <Share />}
                        label={action.action_type === 'qr_code' ? 'QR Code' : 'Share'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {action.template || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={action.visibility === 'all_roles' ? 'All Roles' : 'Specific Roles'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{action.sort_order}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => openEditDialog(action)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(action.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Custom Action' : 'Add Custom Action'}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Object</InputLabel>
            <Select
              value={form.object_id}
              onChange={(e) => setForm((p) => ({ ...p, object_id: e.target.value }))}
              label="Object"
            >
              {objects
                .sort((a, b) => {
                  if (a.is_system !== b.is_system) return b.is_system - a.is_system
                  return a.label.localeCompare(b.label)
                })
                .map((obj) => (
                  <MenuItem key={obj.id} value={obj.id}>
                    {obj.is_system ? '⭐ ' : ''}{obj.label}
                    {!obj.is_system && ' (Custom)'}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            label="Name (identifier)"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            placeholder="e.g. share_facebook"
            helperText="English identifier, unique within the object"
          />
          <TextField
            label="Label (display name)"
            value={form.label}
            onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            placeholder="e.g. Share to Facebook"
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Action Type</InputLabel>
            <Select
              value={form.action_type}
              onChange={(e) => setForm((p) => ({ ...p, action_type: e.target.value as ActionType }))}
              label="Action Type"
            >
              {ACTION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {opt.icon}
                    {opt.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Template"
            value={form.template}
            onChange={(e) => setForm((p) => ({ ...p, template: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            multiline
            rows={3}
            placeholder={`Name: {{name}}\nStatus: {{status}}`}
            helperText={selectedObjectFields ? `Use {{field_name}} to insert values. Available fields from: ${selectedObjectFields}` : 'Select an object first to see available fields'}
          />
          <Autocomplete
            size="small"
            options={ICON_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            value={ICON_OPTIONS.find((o: typeof ICON_OPTIONS[number]) => o.value === form.icon) ?? null}
            onChange={(_, newVal) => setForm((p) => ({ ...p, icon: newVal?.value ?? '' }))}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            sx={{ mt: 2 }}
            renderOption={(props, opt) => {
              const IconComp = opt.Component
              return (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconComp fontSize="small" />
                    {opt.label}
                  </Box>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField {...params} label="Icon (optional)" variant="outlined" />
            )}
          />
          <TextField
            label="Sort Order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) || 0 }))}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
            helperText="Lower values appear first"
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Visibility</InputLabel>
            <Select
              value={form.visibility}
              onChange={(e) => setForm((p) => ({ ...p, visibility: e.target.value as ActionVisibility }))}
              label="Visibility"
            >
              <MenuItem value="all_roles">All Roles</MenuItem>
              <MenuItem value="specific_roles">Specific Roles</MenuItem>
            </Select>
          </FormControl>
          {form.visibility === 'specific_roles' && roles.length > 0 && (
            <Box sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>Visible to roles:</Typography>
              <FormGroup row>
                {roles.map((role) => (
                  <FormControlLabel
                    key={role.id}
                    control={
                      <Checkbox
                        checked={form.role_ids.includes(role.id)}
                        onChange={(e) => {
                          setForm((p) => ({
                            ...p,
                            role_ids: e.target.checked
                              ? [...p.role_ids, role.id]
                              : p.role_ids.filter((id) => id !== role.id),
                          }))
                        }}
                        size="small"
                      />
                    }
                    label={role.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !isFormValid}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Delete Custom Action"
        message="Are you sure you want to delete this custom action? It will be removed from the object's detail view."
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() => handleDelete(deleteConfirm!)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  )
}
